import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ScoreService {
  public radiusMeters = signal(25);

  /**
   * Factor de potencia para la formula del score.
   * Se calculo para que al 50% del radio el score sea ~ 8 (en lugar de 5).
   * Formula: k = log(0.3) / log(0.5) ≈ 1.74
   *
   * Verificacion:
   *   score = 10 * (1 - 0.5^2.32) ≈ 8
   *   score = 10 * (1 - 0.2) = 8
   */
  readonly SCORE_POWER_FACTOR = 2.32;

  public getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    return this.haversineDistance(lat1, lon1, lat2, lon2);
  }

  public calculateScore(
    userCoords: [number, number] | null,
    formularioCoords: [number, number] | null,
    evidenciasCoords: [number, number] | null,
    mainLat: number,
    mainLng: number
  ): { score: string; color: 'success' | 'warning' | 'danger' | 'primary'; description: string } {
    const radius = this.radiusMeters();
    const points: number[] = [];

    if (userCoords) {
      const dist = this.haversineDistance(mainLat, mainLng, userCoords[0], userCoords[1]);
      points.push(this.calculatePointScore(dist, radius));
    }

    if (formularioCoords) {
      const dist = this.haversineDistance(mainLat, mainLng, formularioCoords[0], formularioCoords[1]);
      points.push(this.calculatePointScore(dist, radius));
    }

    if (evidenciasCoords) {
      const dist = this.haversineDistance(mainLat, mainLng, evidenciasCoords[0], evidenciasCoords[1]);
      points.push(this.calculatePointScore(dist, radius));
    }

    if (points.length === 0) {
      return { score: 'N/A', color: 'primary', description: 'Sin coordenadas para evaluar' };
    }

    const avgScore = points.reduce((a, b) => a + b, 0) / points.length;
    const scoreText = `${avgScore.toFixed(0)}/10`;

    let color: 'success' | 'warning' | 'danger' | 'primary' = 'primary';
    let description = '';

    if (avgScore >= 8) {
      color = 'success';
      description = 'Coordenadas dentro del radio';
    } else if (avgScore >= 6) {
      color = 'warning';
      description = 'Revisar coordenadas fuera del radio';
    } else {
      color = 'danger';
      description = 'Coordenadas fuera del radio';
    }

    return { score: scoreText, color, description };
  }

  public calculateRiskLevel(
    userCoords: [number, number] | null,
    formularioCoords: [number, number] | null,
    evidenciasCoords: [number, number] | null,
    mainLat: number,
    mainLng: number
  ): { level: string; color: 'success' | 'warning' | 'danger' | 'primary'; description: string } {
    const radius = this.radiusMeters();
    const points: number[] = [];

    if (userCoords) {
      const dist = this.haversineDistance(mainLat, mainLng, userCoords[0], userCoords[1]);
      points.push(this.calculatePointScore(dist, radius));
    }

    if (formularioCoords) {
      const dist = this.haversineDistance(mainLat, mainLng, formularioCoords[0], formularioCoords[1]);
      points.push(this.calculatePointScore(dist, radius));
    }

    if (evidenciasCoords) {
      const dist = this.haversineDistance(mainLat, mainLng, evidenciasCoords[0], evidenciasCoords[1]);
      points.push(this.calculatePointScore(dist, radius));
    }

    if (points.length === 0) {
      return { level: 'N/A', color: 'primary', description: 'Sin coordenadas para evaluar' };
    }

    const avgScore = points.reduce((a, b) => a + b, 0) / points.length;

    if (avgScore >= 8) {
      return { level: 'Bajo', color: 'success', description: 'Historial crediticio favorable' };
    } else if (avgScore >= 6) {
      return { level: 'Medio', color: 'warning', description: 'Revisar coordenadas fuera del radio' };
    } else {
      return { level: 'Alto', color: 'danger', description: 'Coordenadas fuera del radio definido' };
    }
  }

    /**
   * Calcula la distancia en metros entre dos puntos usando la formula de Haversine.
   *
   * La formula de Haversine determina la distancia del circulo grande entre dos puntos
   * en una esfera dado sus latitudes y longitudes.
   *
   * Formula:
   *   a = sin²(dLat/2) + cos(lat1) * cos(lat2) * sin²(dLon/2)
   *   c = 2 * atan2(√a, √(1-a))
   *   distancia = R * c
   *
   * Donde:
   *   R = 6371000m (radio de la Tierra)
   *   dLat = lat2 - lat1
   *   dLon = lon2 - lon1
   *
   * @param lat1 Latitud del punto 1 en grados
   * @param lon1 Longitud del punto 1 en grados
   * @param lat2 Latitud del punto 2 en grados
   * @param lon2 Longitud del punto 2 en grados
   * @returns Distancia en metros entre los dos puntos
   */
  private haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Radio de la Tierra en metros
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  /**
   * Calcula el score (0-10) basado en la distancia de un punto respecto al radio.
   *
   * Formula:
   *   score = max(0, min(10, 10 * (1 - (distancia/radio)^k)))
   *
   * Donde k = SCORE_POWER_FACTOR = 1.74
   *
   * Esta formula usa una curva de potencia para penalizar menos las distancias
   * cercanas al centro y mas las cercanas al limite del radio.
   *
   * Ejemplo con radio = 100m:
   *   distancia = 0m  → score = 10 (punto en el centro)
   *   distancia = 50m  → score ≈ 7  (50% del radio)
   *   distancia = 100m → score = 0  (en el limite)
   *   distancia > 100m → score = 0  (fuera del radio, clampeado)
   *
   * @param dist Distancia en metros del punto a evaluar
   * @param radius Radio en metros
   * @returns Score de 0 a 10
   */
  private calculatePointScore(dist: number, radius: number): number {
    return Math.max(0, Math.min(10, 10 * (1 - Math.pow(dist / radius, this.SCORE_POWER_FACTOR))));
  }

}
